import { Breakpoints } from '@angular/cdk/layout';
import { Component, Inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, NEVER } from 'rxjs';

import { FormManagerService } from '@sim/form';
import {
  BaseTableComponent,
  makeQueryBuilder,
  makeQueryRules,
  TableField,
  TableFilterType,
  TableSelection,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { SourceBibApiService, SwalService } from '@atmi/core';
import {
  SourceBib,
  SourceBibCard,
  SourceBibliographic,
  SourceBibliographicFilter,
  SourceBibliographicFilterMeta,
  SourceBibMeta,
} from '@atmi/core/models';
import { SourceBibliographicFiltersComponent } from '../filters/source-bibliographic-filters/source-bibliographic-filters.component';

export interface SourceBibDialogData {
  sourceBib?: SourceBibCard;
  order?: number;
}

@Component({
  selector: 'app-source-bib-dialog',
  styleUrls: ['./source-bib-dialog.component.scss'],
  templateUrl: './source-bib-dialog.component.html',
})
export class SourceBibDialogComponent
  extends BaseTableComponent<SourceBibliographic>
  implements UiTableComponent<SourceBibliographic> {

  readonly MobileFiltersComponent = SourceBibliographicFiltersComponent;

  readonly fields: TableField<SourceBibliographic>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      header: this.translate.instant('Titolo'),
      key: 'titolo',
    },
    {
      header: this.translate.instant('Tipo'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'tipo',
    },
    {
      header: this.translate.instant('Codice Unico'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'codiceUnico',
    },
    {
      context: (x) => x.autore && x.autore.join(', '),
      header: this.translate.instant('Autore'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'autore',
    },
    {
      header: this.translate.instant('Anno Edizione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'annoEdizione',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      sortable: true,
      template: 'date',
    },
  ];

  readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
    this.sourceBibApi.index({ page, limit, sort, filter })
  );
  readonly sourceBibForm: UntypedFormGroup;

  readonly title = this.data.sourceBib
    ? this.translate.instant('Modifica fonte bibliografica')
    : this.translate.instant('Aggiungi fonte bibliografica');

  readonly mainActionLabel = this.data.sourceBib
    ? this.translate.instant('Sostituisci')
    : this.translate.instant('Collega');

  constructor(
    private readonly dialogRef: MatDialogRef<SourceBibDialogComponent>,
    private readonly formManager: FormManagerService,
    private readonly sourceBibApi: SourceBibApiService,
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    @Inject(MAT_DIALOG_DATA) public readonly data: SourceBibDialogData,
  ) {
    super(translate, router, route, swalService, undefined, 'local');

    if (this.data.sourceBib?.docBib?.id) {
      const sourceBib: SourceBib = {
        ...this.data.sourceBib,
        docBib: this.data.sourceBib.docBib.id,
      };

      this.selectedItems = [this.data.sourceBib.docBib];
      this.sourceBibForm = this.formManager.makeForm(SourceBibMeta, sourceBib);
    } else if (this.data.order) {
      this.sourceBibForm = this.formManager.makeForm(SourceBibMeta, { ordine: this.data.order + 1, riferimento: '', docBib: '' });
    } else {
      throw new Error('Cannot set source bib without an order');
    }
  }

  completeHandler() {
    if (this.sourceBibForm.valid) {
      this.dialogRef.close(this.sourceBibForm.value);
    } else {
      this.sourceBibForm.markAllAsTouched();
    }
  }

  selectHandler(selectedNews: TableSelection<SourceBibliographic>) {
    super.selectHandler(selectedNews);
    if (selectedNews.selected.length > 0) {
      this.sourceBibForm.controls.docBib.setValue(selectedNews.selected[0].id);
    }
  }

  closeHandler() {
    this.dialogRef.close();
  }

  deleteConcreteHandler() {
    return NEVER;
  }

  deleteSelectedConcreteHandler() {
    return NEVER;
  }

  applyFilterWithDelayHandler(filters: SourceBibliographicFilter) {
    const rules = makeQueryRules(filters, SourceBibliographicFilterMeta);
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }
}
